import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { PairListItem } from '../services/pairTradingApi';
import { resolveSelectedPairKey } from '../pages/PairTrading/utils/pairSelection';

interface UsePairUrlStateArgs {
    portfoliosLoaded: boolean;
    portfolioIds: string[];
    selectedPortfolioId: string;
    setSelectedPortfolioId: (id: string) => void;
    /** The portfolio's pairs; null while loading. */
    pairs: PairListItem[] | null;
    currentDataConfigId: string | null;
}

/**
 * `/pair-trading?portfolio=<id>&pair=<dataConfigId|new>` — so back/forward, reload and a
 * shared link all open the same pair. The portfolio selection itself stays owned by
 * usePortfolios; this only mirrors it both ways.
 */
export function usePairUrlState({
    portfoliosLoaded,
    portfolioIds,
    selectedPortfolioId,
    setSelectedPortfolioId,
    pairs,
    currentDataConfigId,
}: UsePairUrlStateArgs) {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const urlPortfolio = searchParams.get('portfolio');
    const urlPair = searchParams.get('pair');
    // `pair` only means something once the URL describes the selected portfolio.
    const urlMatches = !!selectedPortfolioId && urlPortfolio === selectedPortfolioId;
    const selectedKey = selectedPortfolioId
        ? resolveSelectedPairKey({
            param: urlMatches ? urlPair : null,
            pairs,
            currentDataConfigId,
        })
        : null;

    // setSearchParams navigates without state; carry the route state (strategyId,
    // executionId) over or the page loses it on the first pair switch.
    const stateRef = useRef(location.state);
    stateRef.current = location.state;

    const update = useCallback(
        (mutate: (next: URLSearchParams) => void, replace: boolean) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    mutate(next);
                    return next;
                },
                { replace, state: stateRef.current },
            );
        },
        [setSearchParams],
    );

    // URL → selection: back/forward or a shared link naming another portfolio.
    useEffect(() => {
        if (!portfoliosLoaded || !urlPortfolio) return;
        if (urlPortfolio !== selectedPortfolioId && portfolioIds.includes(urlPortfolio)) {
            setSelectedPortfolioId(urlPortfolio);
        }
        // Only a URL change may move the selection; a selection change is handled below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlPortfolio, portfoliosLoaded]);

    // Selection → URL. A different portfolio has different pairs, so `pair` is dropped.
    useEffect(() => {
        if (!selectedPortfolioId || selectedPortfolioId === urlPortfolio) return;
        update((next) => {
            next.set('portfolio', selectedPortfolioId);
            next.delete('pair');
        }, !urlPortfolio);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPortfolioId]);

    // Write the resolved pair back (absent or unknown `pair`), without a history entry.
    useEffect(() => {
        if (!selectedKey || !urlMatches || urlPair === selectedKey) return;
        update((next) => next.set('pair', selectedKey), true);
    }, [selectedKey, urlPair, urlMatches, update]);

    /** User picked a pair: a history entry, so Back returns to the previous one. */
    const selectPair = useCallback(
        (key: string, opts: { replace?: boolean } = {}) => {
            update((next) => next.set('pair', key), !!opts.replace);
        },
        [update],
    );

    /** Forget the explicit pair; the page falls back to the portfolio's current pair. */
    const clearPair = useCallback(() => {
        update((next) => next.delete('pair'), true);
    }, [update]);

    return {
        /** dataConfigId, `new`, or null while the list is loading. */
        selectedKey,
        selectPair,
        clearPair,
    };
}

export default usePairUrlState;
